import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import ColorPreviewInput from 'flarum/common/components/ColorPreviewInput';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import Stream from 'flarum/common/utils/Stream';
import extractText from 'flarum/common/utils/extractText';

import { apiUrl, clubForDiscussion, toSummary, updateClubSummary } from '../../common/clubs';

/**
 * Icons offered for a club badge. All exist in the Font Awesome 5.15 set that
 * Flarum 1.8 ships, so nobody has to know or type a class name.
 */
const ICONS = [
  'fas fa-book', 'fas fa-book-open', 'fas fa-feather-alt', 'fas fa-pen-nib', 'fas fa-graduation-cap', 'fas fa-brain',
  'fas fa-film', 'fas fa-theater-masks', 'fas fa-music', 'fas fa-guitar', 'fas fa-microphone', 'fas fa-camera',
  'fas fa-palette', 'fas fa-paint-brush', 'fas fa-puzzle-piece', 'fas fa-chess', 'fas fa-gamepad', 'fas fa-laptop',
  'fas fa-utensils', 'fas fa-coffee', 'fas fa-apple-alt', 'fas fa-leaf', 'fas fa-seedling', 'fas fa-paw',
  'fas fa-futbol', 'fas fa-running', 'fas fa-dumbbell', 'fas fa-bicycle', 'fas fa-spa', 'fas fa-campground',
  'fas fa-plane', 'fas fa-globe-europe', 'fas fa-heart', 'fas fa-star', 'fas fa-baby', 'fas fa-baby-carriage',
  'fas fa-child', 'fas fa-stethoscope', 'fas fa-hands-helping', 'fas fa-users',
];

/** Preset badge colours; the colour field below still accepts any value. */
const COLORS = ['#e74c3c', '#e67e22', '#f1c40f', '#27ae60', '#16a085', '#2980b9', '#8e44ad', '#d35485', '#8e6e53', '#4a5568'];

/** The member list gets a search box once it is longer than this. */
const SEARCH_THRESHOLD = 6;
const MAX_VICES = 5;
const LOCALE = 'tr';

/**
 * Turn a private discussion into a club, edit it, or turn it back.
 * Shown to users who may manage clubs (admins by default).
 *
 * Attrs: discussion
 */
export default class ClubSettingsModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.club = clubForDiscussion(this.attrs.discussion);
    this.loadingClub = !!this.club;

    this.slug = Stream('');
    this.icon = Stream(ICONS[0]);
    this.color = Stream(COLORS[8]);
    this.description = Stream('');
    this.search = Stream('');

    /** user id => 'president' | 'vice' */
    this.roles = {};

    if (this.club) this.load();
  }

  /**
   * The forum payload has no description, so fetch the full item.
   */
  load() {
    app
      .request({ method: 'GET', url: apiUrl() })
      .then((response) => {
        const item = (response.data || []).find((club) => club.id === this.club.id);

        if (item) this.fill(item);
      })
      .catch(() => {})
      .then(() => {
        this.loadingClub = false;
        m.redraw.sync();

        // The modal opened as a one-line "loading" box and has just grown to
        // full height. On phones the modal scroll container keeps the browser's
        // scroll-anchored offset and shows the form from the middle; start it
        // at the top so the icon picker is what people see first.
        const container = this.element && this.element.closest('.ModalManager');

        if (container) container.scrollTop = 0;
      });
  }

  fill(item) {
    this.slug(item.slug || '');
    this.icon(item.icon || ICONS[0]);
    this.color(item.color || '');
    this.description(item.description || '');

    this.roles = {};
    (item.leaders || []).forEach((leader) => {
      this.roles[String(leader.userId)] = leader.role;
    });
  }

  className() {
    return 'ClubSettingsModal';
  }

  /**
   * Core focuses (and selects) the first text field on open. Here that is the
   * colour field halfway down, which on a phone scrolls the icon picker out
   * of view, so the modal opens at the top instead.
   */
  onready() {}

  title() {
    return app.translator.trans(this.club ? 'happydigital-clubs.forum.settings.edit_title' : 'happydigital-clubs.forum.settings.create_title', {
      title: this.attrs.discussion.title(),
    });
  }

  content() {
    if (this.loadingClub) {
      return <div className="Modal-body">{app.translator.trans('happydigital-clubs.forum.settings.loading')}</div>;
    }

    return (
      <div className="Modal-body">
        {this.club ? null : <p className="helpText">{app.translator.trans('happydigital-clubs.forum.settings.intro')}</p>}

        {this.iconField()}
        {this.colorField()}
        {this.leadersField()}

        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.settings.description_label')}</label>
          <textarea className="FormControl" rows="3" maxlength="1000" bidi={this.description} disabled={this.loading} />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('happydigital-clubs.forum.settings.slug_label')}</label>
          <input className="FormControl" maxlength="100" bidi={this.slug} disabled={this.loading} />
          <div className="helpText">{app.translator.trans('happydigital-clubs.forum.settings.slug_help')}</div>
        </div>

        <div className="Form-group">
          {Button.component(
            { type: 'submit', className: 'Button Button--primary Button--block', loading: this.loading },
            app.translator.trans(this.club ? 'happydigital-clubs.forum.settings.save' : 'happydigital-clubs.forum.settings.create')
          )}
        </div>

        {this.club ? (
          <div className="Form-group">
            {Button.component(
              { className: 'Button Button--danger Button--block', icon: 'fas fa-times', disabled: this.loading, onclick: () => this.removeClub() },
              app.translator.trans('happydigital-clubs.forum.settings.remove')
            )}
          </div>
        ) : null}
      </div>
    );
  }

  iconField() {
    return (
      <div className="Form-group">
        <label>{app.translator.trans('happydigital-clubs.forum.settings.icon_label')}</label>
        <div className="ClubSettingsModal-icons" role="radiogroup">
          {ICONS.map((name) => (
            <button
              type="button"
              role="radio"
              aria-checked={this.icon() === name ? 'true' : 'false'}
              className={classList('Button', 'Button--icon', 'ClubSettingsModal-icon', { active: this.icon() === name })}
              style={this.icon() === name && this.color() ? { '--club-color': this.color() } : undefined}
              disabled={this.loading}
              onclick={() => this.icon(name)}
            >
              {icon(name)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  colorField() {
    return (
      <div className="Form-group">
        <label>{app.translator.trans('happydigital-clubs.forum.settings.color_label')}</label>
        <div className="ClubSettingsModal-colors">
          {COLORS.map((color) => (
            <button
              type="button"
              className={classList('ClubSettingsModal-swatch', { active: this.color() === color })}
              style={{ '--club-color': color }}
              aria-label={color}
              disabled={this.loading}
              onclick={() => this.color(color)}
            />
          ))}
        </div>
        <ColorPreviewInput value={this.color()} oninput={(e) => this.color(e.target.value)} disabled={this.loading} />
      </div>
    );
  }

  /**
   * The discussion's recipients, each with a role picker. Leaders have to be
   * recipients anyway, so offering anyone else would only produce an error.
   */
  leadersField() {
    const recipients = (this.attrs.discussion.recipientUsers() || []).filter(Boolean);
    const query = this.search().trim().toLocaleLowerCase(LOCALE);

    const visible = recipients
      .filter((user) => !query || [user.displayName(), user.username()].some((text) => (text || '').toLocaleLowerCase(LOCALE).indexOf(query) !== -1))
      .sort((a, b) => this.rank(a) - this.rank(b) || a.displayName().localeCompare(b.displayName(), LOCALE, { sensitivity: 'base' }));

    return (
      <div className="Form-group">
        <label>{app.translator.trans('happydigital-clubs.forum.settings.leaders_label')}</label>
        <div className="helpText">{app.translator.trans('happydigital-clubs.forum.settings.leaders_help')}</div>

        {recipients.length > SEARCH_THRESHOLD ? (
          <input
            className="FormControl ClubSettingsModal-search"
            type="search"
            placeholder={extractText(app.translator.trans('happydigital-clubs.forum.settings.search_placeholder'))}
            bidi={this.search}
            onkeydown={(e) => {
              // The modal is a <form>; Enter here must not save it.
              if (e.key === 'Enter') e.preventDefault();
            }}
          />
        ) : null}

        <ul className="ClubSettingsModal-people">
          {visible.map((user) => (
            <li key={user.id()} className="ClubSettingsModal-person">
              {avatar(user)}
              <span className="ClubSettingsModal-name">{username(user)}</span>
              {Select.component({
                className: 'ClubSettingsModal-role',
                options: this.roleOptions(),
                value: this.roles[user.id()] || '',
                disabled: this.loading,
                onchange: (role) => this.setRole(user.id(), role),
              })}
            </li>
          ))}
        </ul>

        {query && !visible.length ? <p className="helpText">{app.translator.trans('happydigital-clubs.forum.settings.no_match')}</p> : null}
      </div>
    );
  }

  roleOptions() {
    return {
      '': extractText(app.translator.trans('happydigital-clubs.forum.settings.role_member')),
      president: extractText(app.translator.trans('happydigital-clubs.forum.settings.role_president')),
      vice: extractText(app.translator.trans('happydigital-clubs.forum.settings.role_vice')),
    };
  }

  /** President first, then vices, then everyone else. */
  rank(user) {
    const role = this.roles[user.id()];

    return role === 'president' ? 0 : role === 'vice' ? 1 : 2;
  }

  /**
   * A club has one president: appointing a new one makes the previous one an
   * ordinary member again.
   */
  setRole(userId, role) {
    const roles = Object.assign({}, this.roles);

    if (role === 'president') {
      Object.keys(roles).forEach((id) => {
        if (roles[id] === 'president') delete roles[id];
      });
    }

    if (role) roles[userId] = role;
    else delete roles[userId];

    this.roles = roles;
    this.alertAttrs = null;
  }

  usernameOf(userId) {
    const user = app.store.getById('users', userId);

    return user ? user.username() : null;
  }

  onsubmit(e) {
    e.preventDefault();

    const vices = Object.keys(this.roles).filter((id) => this.roles[id] === 'vice');

    if (vices.length > MAX_VICES) {
      this.alertAttrs = { type: 'error', content: app.translator.trans('happydigital-clubs.forum.errors.vices_too_many') };

      return;
    }

    const presidentId = Object.keys(this.roles).find((id) => this.roles[id] === 'president');

    const attributes = {
      slug: this.slug(),
      icon: this.icon(),
      color: this.color(),
      description: this.description(),
      president: presidentId ? this.usernameOf(presidentId) : '',
      vices: vices.map((id) => this.usernameOf(id)).filter(Boolean),
    };

    if (!this.club) attributes.discussionId = this.attrs.discussion.id();

    this.loading = true;

    app
      .request({
        method: this.club ? 'PATCH' : 'POST',
        url: apiUrl(this.club ? '/' + this.club.id : ''),
        body: { data: { attributes } },
        errorHandler: (error) => this.showError(error),
      })
      .then((response) => {
        updateClubSummary(response.data.id, toSummary(response.data));
        this.refreshDiscussion();
        this.hide();
      })
      .catch(() => {})
      .then(() => {
        this.loading = false;
        m.redraw();
      });
  }

  removeClub() {
    if (!confirm(extractText(app.translator.trans('happydigital-clubs.forum.settings.remove_confirm')))) return;

    this.loading = true;

    app
      .request({ method: 'DELETE', url: apiUrl('/' + this.club.id) })
      .then(() => {
        updateClubSummary(this.club.id, null);
        this.refreshDiscussion();
        this.hide();
      })
      .catch(() => {})
      .then(() => {
        this.loading = false;
        m.redraw();
      });
  }

  /**
   * Badges come with each serialized user, so reload the discussion's posts
   * to show the new member / leader badges straight away.
   */
  refreshDiscussion() {
    app.store.find('discussions', this.attrs.discussion.id()).catch(() => {});

    if (app.current && app.current.get('stream')) {
      app.current.get('stream').update();
    }
  }

  /**
   * Turn the editor's validation codes ("taken", "not_a_member:ali") into
   * readable messages.
   */
  showError(error) {
    const errors = (error.response && error.response.errors) || [];
    const first = errors[0];

    if (!first || !first.source || !first.source.pointer) {
      this.onerror(error);

      return;
    }

    const field = first.source.pointer.split('/').pop();
    const [code, detail] = String(first.detail || '').split(':');

    this.alertAttrs = {
      type: 'error',
      content: app.translator.trans(`happydigital-clubs.forum.errors.${field}_${code}`, { value: detail || '' }),
    };

    m.redraw();
  }
}
